import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTrash,
  FaSearch,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaInbox,
  FaArrowLeft,
  FaArrowRight,
  FaFilter,
  FaEye,
  FaUser,
  FaStickyNote,
  FaTimes,
  FaCheck,
  FaHashtag,
  FaCut,
  FaLayerGroup,
  FaCommentDots,
} from "react-icons/fa";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../server";
import Loading from "../Components/global/Loading";
import { useDispatch, useSelector } from "react-redux";
import { getAllBookingRequests } from "../redux/actions/contents";
import PageName from "../Components/global/PageName";

export default function BookingRequests() {
  const dispatch = useDispatch();
  const { bookingRequestsList, bookingRequestsCount, loading: bookingsLoading } = useSelector(
    (state) => state.content
  );
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(10);
  const [searchParams, setSearchParams] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sync, setSync] = useState(0);

  // Detailed Modal state
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getAllBookingRequests(pageNo, pageSize, searchParams, statusFilter));
  }, [dispatch, pageNo, pageSize, searchParams, statusFilter, sync]);

  const handleOpenDetail = async (id) => {
    if (!id) return;
    try {
      setDetailLoading(true);
      setIsModalOpen(true);
      const { data: res } = await axios.get(
        `${server}/bookingRequest/getById`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            bookingRequestId: id,
          },
        }
      );
      if (res?.success) {
        setSelectedBooking(res?.data);
      } else {
        toast.error(res?.message || "Failed to fetch booking request details.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Error fetching details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const userRes = window.confirm(
      "Are you sure you want to delete this booking request?"
    );
    if (!userRes) return;

    try {
      setLoading(true);
      const { data: res } = await axios.delete(
        `${server}/bookingRequest/deleteById`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            bookingRequestId: id,
          },
        }
      );
      toast.success(res?.message || "Booking request deleted.");
      if (isModalOpen && selectedBooking?.id === id) {
        setIsModalOpen(false);
        setSelectedBooking(null);
      }
      setSync((prev) => prev + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      setLoading(true);
      const { data: res } = await axios.put(
        `${server}/bookingRequest/updateStatus`,
        null,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            bookingRequestId: id,
            status: newStatus,
          },
        }
      );
      toast.success(res?.message || `Status updated to ${newStatus}`);
      if (selectedBooking && selectedBooking.id === id) {
        setSelectedBooking((prev) => ({ ...prev, status: newStatus }));
      }
      setSync((prev) => prev + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "N/A";
    let d;
    if (!isNaN(dateStr) && !isNaN(parseFloat(dateStr))) {
      d = new Date(Number(dateStr));
    } else {
      d = new Date(dateStr);
    }
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTimeDisplay = (dateStr) => {
    if (!dateStr) return "N/A";
    let d = new Date(dateStr);
    if (isNaN(d.getTime()) && !isNaN(dateStr) && !isNaN(parseFloat(dateStr))) {
      d = new Date(Number(dateStr));
    }
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderStatusBadge = (status) => {
    const s = (status || "new").toLowerCase();
    if (s === "confirmed" || s === "accepted") {
      return (
        <span className="inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[0.68rem] font-bold px-3 py-1 rounded-full uppercase">
          <FaCheckCircle size={10} /> Confirmed
        </span>
      );
    } else if (s === "cancelled" || s === "rejected") {
      return (
        <span className="inline-flex items-center gap-1 bg-rose-500/15 text-rose-400 border border-rose-500/30 text-[0.68rem] font-bold px-3 py-1 rounded-full uppercase">
          <FaTimesCircle size={10} /> Cancelled
        </span>
      );
    } else if (s === "completed") {
      return (
        <span className="inline-flex items-center gap-1 bg-purple-500/15 text-purple-400 border border-purple-500/30 text-[0.68rem] font-bold px-3 py-1 rounded-full uppercase">
          <FaCheckCircle size={10} /> Completed
        </span>
      );
    } else if (s === "new") {
      return (
        <span className="inline-flex items-center gap-1 bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 text-[0.68rem] font-bold px-3 py-1 rounded-full uppercase">
          <FaClock size={10} /> New Request
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 bg-amber-500/15 text-amber-400 border border-amber-500/30 text-[0.68rem] font-bold px-3 py-1 rounded-full uppercase">
          <FaClock size={10} /> {status}
        </span>
      );
    }
  };

  const totalPages = Math.ceil((bookingRequestsCount || 0) / pageSize) || 1;

  return (
    <div className="min-h-screen pb-16 bg-[#0A0A0C] text-[#F8F5F2]">
      <PageName
        title="Booking Requests"
        subTitle="Review, approve, and manage all client appointment inquiries in detail."
      />

      <div className="max-w-[96rem] mx-auto px-4 mt-4">
        
        {/* Filter & Search Bar */}
        <div className="bg-[#121215]/90 backdrop-blur-2xl p-4 rounded-3xl border border-[#C8A165]/25 shadow-lg mb-6 flex flex-col md:flex-row items-center justify-between gap-4">
          
          {/* Search Box */}
          <div className="relative w-full md:w-80">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#C8A165]/60 text-xs" />
            <input
              type="text"
              placeholder="Search by name, mobile, email, service..."
              value={searchParams}
              onChange={(e) => {
                setSearchParams(e.target.value);
                setPageNo(1);
              }}
              className="w-full text-xs pl-10 pr-4 py-2.5 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] placeholder:text-[#F8F5F2]/30 transition-all"
            />
          </div>

          {/* Status Filter Tabs */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[0.68rem] uppercase font-bold text-[#C8A165] flex items-center gap-1 mr-1">
              <FaFilter size={10} /> Status:
            </span>
            {["All", "New", "Confirmed", "Cancelled", "Completed"].map((st) => (
              <button
                key={st}
                onClick={() => {
                  setStatusFilter(st);
                  setPageNo(1);
                }}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                  statusFilter === st
                    ? "bg-[#C8A165] text-[#0A0A0C] font-bold shadow-md"
                    : "bg-[#18181D] text-[#F8F5F2]/60 hover:text-[#C8A165] border border-[#C8A165]/15"
                }`}
              >
                {st}
              </button>
            ))}
          </div>

        </div>

        {/* Booking Requests Table */}
        <div className="overflow-x-auto rounded-3xl shadow-[0_15px_40px_rgba(0,0,0,0.7)] border border-[#C8A165]/25 bg-[#121215]/90 backdrop-blur-2xl text-left">
          <table className="min-w-full divide-y divide-[#C8A165]/15 text-xs">
            <thead className="bg-[#18181D] text-[#C8A165] uppercase tracking-widest font-bold">
              <tr>
                <th className="px-4 py-4 text-center"># ID</th>
                <th className="px-5 py-4 text-left">Client Details</th>
                <th className="px-5 py-4 text-left">Service & Event</th>
                <th className="px-5 py-4 text-left">Booking Date & Location</th>
                <th className="px-4 py-4 text-center">Add-ons</th>
                <th className="px-5 py-4 text-left">Notes & Remarks</th>
                <th className="px-4 py-4 text-center">Created At</th>
                <th className="px-4 py-4 text-center">Status</th>
                <th className="px-4 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#C8A165]/10">
              {bookingRequestsList?.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-16 text-[#F8F5F2]/40">
                    <FaInbox size={32} className="mx-auto mb-2 text-[#C8A165]" />
                    No booking requests found matching your filter.
                  </td>
                </tr>
              ) : (
                bookingRequestsList?.map((item, index) => (
                  <motion.tr
                    key={item?.id || index}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.04 }}
                    onClick={() => handleOpenDetail(item?.id)}
                    className="hover:bg-[#C8A165]/10 transition-colors cursor-pointer group"
                  >
                    {/* ID */}
                    <td className="px-4 py-4 text-center font-mono font-bold text-[#C8A165]">
                      #{item?.id || "N/A"}
                    </td>

                    {/* Client Info */}
                    <td className="px-5 py-4">
                      <div className="font-bold text-[#F8F5F2] text-sm group-hover:text-[#C8A165] transition-colors flex items-center gap-1.5">
                        <FaUser size={10} className="text-[#C8A165]" />
                        {item?.name || item?.fullName || "Client Name"}
                      </div>
                      <div className="flex flex-col gap-1 mt-1 text-[#F8F5F2]/60 text-[0.7rem]">
                        <a
                          href={`tel:${item?.mobile || item?.mobileNo || ""}`}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1.5 hover:text-[#C8A165]"
                        >
                          <FaPhoneAlt size={10} className="text-[#C8A165]" />
                          {item?.mobile || item?.mobileNo || "N/A"}
                        </a>
                        {item?.email && (
                          <a
                            href={`mailto:${item?.email}`}
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1.5 hover:text-[#C8A165]"
                          >
                            <FaEnvelope size={10} className="text-[#C8A165]" />
                            {item?.email}
                          </a>
                        )}
                      </div>
                    </td>

                    {/* Service & Event */}
                    <td className="px-5 py-4 text-[#F8F5F2]/90 font-medium">
                      <div className="flex flex-col gap-1">
                        <span className="px-2.5 py-0.5 rounded-lg bg-[#18181D] border border-[#C8A165]/30 text-[#C8A165] font-semibold text-[0.7rem] w-fit">
                          {item?.service || item?.serviceType || "Bridal Service"}
                        </span>
                        {item?.event && (
                          <span className="text-[#F8F5F2]/70 text-[0.68rem] flex items-center gap-1">
                            <FaLayerGroup size={9} className="text-[#C8A165]" />
                            {item?.event}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Event Date & Location */}
                    <td className="px-5 py-4 text-[#F8F5F2]/80">
                      <div className="flex items-center gap-1.5 font-semibold text-[#C8A165]">
                        <FaCalendarAlt size={11} />
                        <span>{formatDateDisplay(item?.bookingDate || item?.eventDate)}</span>
                      </div>
                      <div className="flex items-center gap-1 text-[#F8F5F2]/60 text-[0.68rem] mt-1">
                        <FaMapMarkerAlt size={10} className="text-[#C8A165]/80" />
                        <span>{item?.location || item?.venue || "Studio / On-Site"}</span>
                      </div>
                    </td>

                    {/* Add-ons */}
                    <td className="px-4 py-4 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span
                          className={`px-2 py-0.5 rounded text-[0.62rem] font-bold ${
                            item?.hairstyling
                              ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                              : "bg-[#18181D] text-[#F8F5F2]/40"
                          }`}
                        >
                          Hair: {item?.hairstyling ? "Yes" : "No"}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded text-[0.62rem] font-bold ${
                            item?.draping
                              ? "bg-purple-500/20 text-purple-300 border border-purple-500/30"
                              : "bg-[#18181D] text-[#F8F5F2]/40"
                          }`}
                        >
                          Draping: {item?.draping ? "Yes" : "No"}
                        </span>
                      </div>
                    </td>

                    {/* Notes & Remarks */}
                    <td className="px-5 py-4 max-w-[200px]">
                      {item?.additionalNotes ? (
                        <div className="text-[0.68rem] text-[#F8F5F2]/80 truncate flex items-center gap-1" title={item?.additionalNotes}>
                          <FaStickyNote size={10} className="text-[#C8A165] flex-shrink-0" />
                          <span className="truncate">{item?.additionalNotes}</span>
                        </div>
                      ) : (
                        <span className="text-[#F8F5F2]/30 text-[0.65rem]">No Notes</span>
                      )}
                      {item?.remarks && (
                        <div className="text-[0.65rem] text-[#C8A165]/80 truncate mt-1 flex items-center gap-1" title={item?.remarks}>
                          <FaCommentDots size={10} className="flex-shrink-0" />
                          <span className="truncate">{item?.remarks}</span>
                        </div>
                      )}
                    </td>

                    {/* Created At */}
                    <td className="px-4 py-4 text-center text-[#F8F5F2]/60 text-[0.68rem]">
                      {formatDateTimeDisplay(item?.createdAt)}
                    </td>

                    {/* Status Badge */}
                    <td className="px-4 py-4 text-center">
                      {renderStatusBadge(item?.status)}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleOpenDetail(item?.id)}
                          className="p-2 rounded-xl bg-[#C8A165]/15 hover:bg-[#C8A165] text-[#C8A165] hover:text-[#0A0A0C] border border-[#C8A165]/30 transition-all cursor-pointer"
                          title="View Full Details"
                        >
                          <FaEye size={12} />
                        </button>

                        <select
                          value={item?.status || "new"}
                          onChange={(e) => handleUpdateStatus(item?.id, e.target.value)}
                          className="bg-[#18181D] text-[#C8A165] border border-[#C8A165]/30 rounded-xl px-2 py-1 text-[0.68rem] font-bold focus:outline-none focus:border-[#C8A165] cursor-pointer"
                        >
                          <option value="new">New</option>
                          <option value="Confirmed">Confirmed</option>
                          <option value="Cancelled">Cancelled</option>
                          <option value="Completed">Completed</option>
                        </select>

                        <button
                          onClick={() => handleDelete(item?.id)}
                          className="p-2 rounded-xl text-rose-400 hover:bg-rose-500/10 border border-rose-500/20 transition-all cursor-pointer"
                          title="Delete Request"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {totalPages > 1 && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#121215]/90 backdrop-blur-2xl p-4 rounded-2xl border border-[#C8A165]/25 shadow-lg">
            <span className="text-xs text-[#F8F5F2]/50">
              Page <strong className="text-[#C8A165]">{pageNo}</strong> of{" "}
              <strong className="text-[#C8A165]">{totalPages}</strong> ({bookingRequestsCount || 0} total requests)
            </span>

            <div className="flex items-center gap-2">
              <button
                disabled={pageNo <= 1}
                onClick={() => setPageNo((prev) => Math.max(1, prev - 1))}
                className="px-3.5 py-1.5 rounded-xl border border-[#C8A165]/30 text-xs font-bold text-[#C8A165] hover:bg-[#C8A165] hover:text-[#0A0A0C] disabled:opacity-30 disabled:pointer-events-none transition-all cursor-pointer flex items-center gap-1"
              >
                <FaArrowLeft size={10} /> Prev
              </button>

              <button
                disabled={pageNo >= totalPages}
                onClick={() => setPageNo((prev) => Math.min(totalPages, prev + 1))}
                className="px-3.5 py-1.5 rounded-xl border border-[#C8A165]/30 text-xs font-bold text-[#C8A165] hover:bg-[#C8A165] hover:text-[#0A0A0C] disabled:opacity-30 disabled:pointer-events-none transition-all cursor-pointer flex items-center gap-1"
              >
                Next <FaArrowRight size={10} />
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Detailed View Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[#121215] border border-[#C8A165]/30 text-[#F8F5F2] rounded-3xl p-6 sm:p-8 w-full max-w-3xl shadow-[0_25px_60px_rgba(0,0,0,0.9)] max-h-[90vh] overflow-y-auto relative"
            >
              {/* Close Button */}
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-6 right-6 p-2 text-[#F8F5F2]/50 hover:text-[#C8A165] bg-[#18181D] border border-[#C8A165]/20 rounded-full transition-all cursor-pointer"
              >
                <FaTimes size={14} />
              </button>

              {detailLoading ? (
                <div className="py-16 text-center text-[#C8A165]">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#C8A165] mb-3"></div>
                  <p className="text-xs tracking-wider">Fetching details from GetById API...</p>
                </div>
              ) : selectedBooking ? (
                <div>
                  {/* Modal Header */}
                  <div className="flex flex-wrap items-center justify-between gap-3 pb-4 mb-6 border-b border-[#C8A165]/20">
                    <div>
                      <span className="text-[0.68rem] uppercase font-bold text-[#C8A165] tracking-widest flex items-center gap-1">
                        <FaHashtag size={10} /> Booking Request #{selectedBooking?.id}
                      </span>
                      <h2 className="text-xl sm:text-2xl font-bold text-[#F8F5F2] mt-1">
                        {selectedBooking?.name || "Client Details"}
                      </h2>
                    </div>
                    <div>
                      {renderStatusBadge(selectedBooking?.status)}
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
                    
                    {/* Client Info Box */}
                    <div className="bg-[#18181D] p-4 rounded-2xl border border-[#C8A165]/15">
                      <h3 className="text-[#C8A165] font-bold uppercase tracking-wider text-[0.7rem] mb-3 flex items-center gap-1.5">
                        <FaUser size={11} /> Client Contact Info
                      </h3>
                      <div className="space-y-2 text-[#F8F5F2]/80">
                        <div>
                          <span className="text-[#F8F5F2]/40 block text-[0.65rem] uppercase">Full Name</span>
                          <span className="font-semibold text-sm text-[#F8F5F2]">{selectedBooking?.name || "N/A"}</span>
                        </div>
                        <div>
                          <span className="text-[#F8F5F2]/40 block text-[0.65rem] uppercase">Mobile Phone</span>
                          <a href={`tel:${selectedBooking?.mobile}`} className="font-semibold text-[#C8A165] hover:underline flex items-center gap-1.5">
                            <FaPhoneAlt size={10} /> {selectedBooking?.mobile || "N/A"}
                          </a>
                        </div>
                        <div>
                          <span className="text-[#F8F5F2]/40 block text-[0.65rem] uppercase">Email Address</span>
                          {selectedBooking?.email ? (
                            <a href={`mailto:${selectedBooking?.email}`} className="font-semibold text-[#C8A165] hover:underline flex items-center gap-1.5">
                              <FaEnvelope size={10} /> {selectedBooking?.email}
                            </a>
                          ) : (
                            <span>N/A</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Event & Booking Box */}
                    <div className="bg-[#18181D] p-4 rounded-2xl border border-[#C8A165]/15">
                      <h3 className="text-[#C8A165] font-bold uppercase tracking-wider text-[0.7rem] mb-3 flex items-center gap-1.5">
                        <FaCalendarAlt size={11} /> Appointment & Event
                      </h3>
                      <div className="space-y-2 text-[#F8F5F2]/80">
                        <div>
                          <span className="text-[#F8F5F2]/40 block text-[0.65rem] uppercase">Service Requested</span>
                          <span className="font-semibold text-[#C8A165]">{selectedBooking?.service || "N/A"}</span>
                        </div>
                        <div>
                          <span className="text-[#F8F5F2]/40 block text-[0.65rem] uppercase">Event Type</span>
                          <span className="font-semibold">{selectedBooking?.event || "N/A"}</span>
                        </div>
                        <div>
                          <span className="text-[#F8F5F2]/40 block text-[0.65rem] uppercase">Requested Date</span>
                          <span className="font-semibold">{formatDateDisplay(selectedBooking?.bookingDate)}</span>
                        </div>
                        <div>
                          <span className="text-[#F8F5F2]/40 block text-[0.65rem] uppercase">Location / Venue</span>
                          <span className="font-semibold flex items-center gap-1">
                            <FaMapMarkerAlt size={10} className="text-[#C8A165]" />
                            {selectedBooking?.location || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Add-ons & Customizations */}
                    <div className="bg-[#18181D] p-4 rounded-2xl border border-[#C8A165]/15 md:col-span-2">
                      <h3 className="text-[#C8A165] font-bold uppercase tracking-wider text-[0.7rem] mb-3 flex items-center gap-1.5">
                        <FaCut size={11} /> Hairstyling & Draping Requirements
                      </h3>
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2 bg-[#121215] px-3.5 py-2 rounded-xl border border-[#C8A165]/20">
                          <span className="text-[#F8F5F2]/70 font-semibold">Hairstyling Required:</span>
                          {selectedBooking?.hairstyling ? (
                            <span className="inline-flex items-center gap-1 text-emerald-400 font-bold bg-emerald-500/15 px-2.5 py-0.5 rounded-lg border border-emerald-500/30">
                              <FaCheck size={10} /> Yes
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[#F8F5F2]/40 font-bold bg-[#18181D] px-2.5 py-0.5 rounded-lg">
                              <FaTimes size={10} /> No
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 bg-[#121215] px-3.5 py-2 rounded-xl border border-[#C8A165]/20">
                          <span className="text-[#F8F5F2]/70 font-semibold">Draping Required:</span>
                          {selectedBooking?.draping ? (
                            <span className="inline-flex items-center gap-1 text-purple-300 font-bold bg-purple-500/15 px-2.5 py-0.5 rounded-lg border border-purple-500/30">
                              <FaCheck size={10} /> Yes
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[#F8F5F2]/40 font-bold bg-[#18181D] px-2.5 py-0.5 rounded-lg">
                              <FaTimes size={10} /> No
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Client Additional Notes */}
                    <div className="bg-[#18181D] p-4 rounded-2xl border border-[#C8A165]/15 md:col-span-2">
                      <h3 className="text-[#C8A165] font-bold uppercase tracking-wider text-[0.7rem] mb-2 flex items-center gap-1.5">
                        <FaStickyNote size={11} /> Additional Client Notes
                      </h3>
                      <p className="text-[#F8F5F2]/80 leading-relaxed bg-[#121215] p-3 rounded-xl border border-[#C8A165]/10 whitespace-pre-wrap">
                        {selectedBooking?.additionalNotes || "No additional notes provided by client."}
                      </p>
                    </div>

                    {/* Admin Remarks */}
                    {selectedBooking?.remarks && (
                      <div className="bg-[#18181D] p-4 rounded-2xl border border-[#C8A165]/15 md:col-span-2">
                        <h3 className="text-[#C8A165] font-bold uppercase tracking-wider text-[0.7rem] mb-2 flex items-center gap-1.5">
                          <FaCommentDots size={11} /> Admin Remarks
                        </h3>
                        <p className="text-[#F8F5F2]/80 leading-relaxed bg-[#121215] p-3 rounded-xl border border-[#C8A165]/10 whitespace-pre-wrap">
                          {selectedBooking?.remarks}
                        </p>
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="md:col-span-2 flex flex-wrap items-center justify-between text-[0.68rem] text-[#F8F5F2]/40 pt-2 border-t border-[#C8A165]/10">
                      <span>Submitted At: {formatDateTimeDisplay(selectedBooking?.createdAt)}</span>
                      <span>Deleted Status: {selectedBooking?.deleted ? "Yes (Deleted)" : "Active"}</span>
                    </div>

                  </div>

                  {/* Modal Actions Footer */}
                  <div className="mt-8 pt-4 border-t border-[#C8A165]/20 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[0.68rem] uppercase text-[#C8A165] font-bold">Update Status:</span>
                      {["New", "Confirmed", "Cancelled", "Completed"].map((st) => {
                        const isSelected = (selectedBooking?.status || "new").toLowerCase() === st.toLowerCase();
                        return (
                          <button
                            key={st}
                            disabled={isSelected}
                            onClick={() => handleUpdateStatus(selectedBooking?.id, st)}
                            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                              isSelected
                                ? "bg-[#C8A165] text-[#0A0A0C] font-extrabold shadow-md"
                                : "bg-[#18181D] text-[#F8F5F2]/70 hover:text-[#C8A165] border border-[#C8A165]/20"
                            }`}
                          >
                            {st}
                          </button>
                        );
                      })}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleDelete(selectedBooking?.id)}
                        className="px-4 py-2 rounded-xl text-xs font-bold text-rose-400 bg-rose-500/10 hover:bg-rose-500 hover:text-white border border-rose-500/20 transition-all cursor-pointer flex items-center gap-1.5"
                      >
                        <FaTrash size={12} /> Delete
                      </button>
                      <button
                        onClick={() => setIsModalOpen(false)}
                        className="px-5 py-2 rounded-xl text-xs font-bold bg-[#C8A165] text-[#0A0A0C] hover:bg-[#d9b276] transition-all cursor-pointer"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-12 text-center text-[#F8F5F2]/50">
                  No booking request detail loaded.
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {(loading || bookingsLoading) && <Loading />}
    </div>
  );
}

